// Status de usuário (Parte 3.4 `User.Status`) e tipo de conta como badges localizados.
import { Badge } from '@giyus/ui';
import { useTranslation } from 'react-i18next';

import { isInFuture, useNow } from '../../../lib/dates/useNow';
import { USER_KINDS, USER_STATUSES } from '../api/queries';

type KnownStatus = (typeof USER_STATUSES)[number];
type KnownKind = (typeof USER_KINDS)[number];

function isKnownStatus(value: string): value is KnownStatus {
  return (USER_STATUSES as readonly string[]).includes(value);
}

function isKnownKind(value: string): value is KnownKind {
  return (USER_KINDS as readonly string[]).includes(value);
}

export interface UserStatusBadgeProps {
  readonly status: string;
  readonly lockedUntil?: string | null | undefined;
}

export function UserStatusBadge({ status, lockedUntil }: UserStatusBadgeProps) {
  const { t } = useTranslation('identity');
  const now = useNow();
  // Bloqueio por abuso não é transição de Status (REQ-AUTH-017): mostra "Bloqueado" enquanto `lockedUntil` for futuro.
  const effective = status === 'Active' && isInFuture(lockedUntil, now) ? 'Locked' : status;
  const label = isKnownStatus(effective) ? t(`admin.status.${effective}`) : effective;
  const variant =
    effective === 'Active'
      ? 'default'
      : effective === 'Invited'
        ? 'secondary'
        : effective === 'Locked'
          ? 'destructive'
          : 'outline';
  return (
    <Badge variant={variant} data-testid="user-status">
      {label}
    </Badge>
  );
}

export function UserKindLabel({ kind }: { readonly kind: string }) {
  const { t } = useTranslation('identity');
  return <>{isKnownKind(kind) ? t(`account.kinds.${kind}`) : kind}</>;
}
